import { Button, Form, Modal } from "react-bootstrap";
import { useEffect, useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm } from "react-hook-form";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import {
  getBreaksByYear,
  getSessionsByYear,
  getSettingByYear,
  saveBreaksByYear,
  saveSessionByYear,
  updateSetting,
} from "../../api/settingEDT";
import { toast } from "sonner";
import React from "react";
import { toMinutes } from "../../helper/toMinutes";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import withPermission from "../../core/common/autorisations/withPermission";

type Props = {
  showSettings: boolean;
  onHide: () => void;
};

const academicSettingsSchema = z
  .object({
    workingDays: z
      .array(z.string())
      .min(1, "Sélectionnez au moins un jour ouvrable"),

    seances: z
      .array(
        z.object({
          id: z.string().optional(),
          index: z.number().min(1),
          label: z.string().min(1, "Le libellé est requis"),
          start_time: z
            .string()
            .regex(
              /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
              "Format d'heure invalide (attendu : HH:mm)",
            ),
          end_time: z
            .string()
            .regex(
              /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
              "Format d'heure invalide (attendu : HH:mm)",
            ),
        }),
      )
      .min(1, "Au moins une séance est requise")
      .refine(
        (seances) => {
          // Vérifie que l'heure de fin est supérieure à l'heure de début pour chaque séance
          const invalidSeanceTimes = seances.some(
            ({ start_time, end_time }) =>
              toMinutes(end_time) <= toMinutes(start_time),
          );
          if (invalidSeanceTimes) return false;
          return true;
        },
        {
          message:
            "L'heure de fin de séance doit être supérieure à l'heure de début",
        },
      )
      .refine(
        (seances) => {
          // Vérifie que les séances ne se chevauchent pas
          const seancePeriods = seances
            .map(({ start_time, end_time }) => ({
              start: toMinutes(start_time),
              end: toMinutes(end_time),
            }))
            .sort((a, b) => a.start - b.start);

          for (let i = 0; i < seancePeriods.length - 1; i++) {
            if (seancePeriods[i].end > seancePeriods[i + 1].start) {
              return false;
            }
          }
          return true;
        },
        {
          message: "Les séances ne doivent pas se chevaucher",
        },
      ),

    breaks: z
      .array(
        z.object({
          id: z.string().optional(),
          label: z.string(),
          start: z
            .string()
            .regex(
              /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
              "Format d'heure invalide (attendu : HH:mm)",
            ),
          end: z
            .string()
            .regex(
              /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
              "Format d'heure invalide (attendu : HH:mm)",
            ),
        }),
      )
      .optional()
      .refine(
        (breaks) => {
          if (!breaks) return true;
          const invalidBreakTimes = breaks.some(
            ({ start, end }) => toMinutes(end) <= toMinutes(start),
          );
          if (invalidBreakTimes) return false;
          return true;
        },
        {
          message:
            "L'heure de fin de pause doit être supérieure à l'heure de début",
        },
      )
      .refine(
        (breaks) => {
          if (!breaks) return true;
          const breakPeriods = breaks
            .map(({ start, end }) => ({
              start: toMinutes(start),
              end: toMinutes(end),
            }))
            .sort((a, b) => a.start - b.start);

          for (let i = 0; i < breakPeriods.length - 1; i++) {
            if (breakPeriods[i].end > breakPeriods[i + 1].start) {
              return false;
            }
          }
          return true;
        },
        {
          message: "Les pauses ne doivent pas se chevaucher",
        },
      ),
  })
  .refine(
    (data) => {
      if (!data.breaks || !data.seances) return true;

      const breakPeriods = data.breaks.map(({ start, end }) => ({
        start: toMinutes(start),
        end: toMinutes(end),
      }));

      const seancePeriods = data.seances.map(({ start_time, end_time }) => ({
        start: toMinutes(start_time),
        end: toMinutes(end_time),
      }));

      // Vérifie que les pauses ne chevauchent pas les séances
      for (const breakPeriod of breakPeriods) {
        for (const seancePeriod of seancePeriods) {
          if (
            (breakPeriod.start >= seancePeriod.start &&
              breakPeriod.start < seancePeriod.end) ||
            (breakPeriod.end > seancePeriod.start &&
              breakPeriod.end <= seancePeriod.end) ||
            (breakPeriod.start <= seancePeriod.start &&
              breakPeriod.end >= seancePeriod.end)
          ) {
            return false;
          }
        }
      }
      return true;
    },
    {
      message: "Les pauses ne doivent pas chevaucher les séances",
      path: ["breaks"],
    },
  );

type AcademicSettings = z.infer<typeof academicSettingsSchema>;

const EdtParameterModal = ({ showSettings, onHide }: Props) => {
  const { selectedYear } = useAcademicYear();
  const [initialWorkingDays, setInitialWorkingDays] = useState<string[]>([]);
  const [initialSeances, setInitialSeances] = useState<any[]>([]);
  const [initialBreaks, setInitialBreaks] = useState<any[]>([]);

  // Hook pour gérer le formulaire avec react-hook-form et zod
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isDirty },
  } = useForm<AcademicSettings>({
    resolver: zodResolver(academicSettingsSchema),
  });

  // fonction helper pour renvoyer le nom dans la semaine d'un jour en fonction de son index
  const getDayNameByindex = (index: number): string => {
    const days = [
      "Lundi",
      "Mardi",
      "Mercredi",
      "Jeudi",
      "Vendredi",
      "Samedi",
      "Dimanche",
    ];
    return days[index - 1] || "";
  };

  // Fonction pour récupérer les paramètres EDT d'une année (jours ouvrables)
  const fetchEDTSettings = async () => {
    try {
      if (!selectedYear) return;

      const response = await getSettingByYear(
        selectedYear.id,
        buildPermissionContext(Resource.SETTING_EDT, Action.READ),
      );
      // console.log("response data: ", response.data);
      // Convertit la chaîne JSON reçue en tableau de jours
      const rawDays: string = response.data[0].working_days; // ex: "{\"Lundi\",\"Dimanche\"}"
      const cleanedDays = rawDays.replace(/[{}]/g, ""); // retire accolades
      const daysArray = cleanedDays
        .split(",")
        .map((d) => d.replace(/"/g, "").trim()); // ["Lundi", "Dimanche"]

      // Mapping nom → index (Lundi=1 … Dimanche=7)
      const dayNameToIndex: Record<string, string> = {
        Lundi: "1",
        Mardi: "2",
        Mercredi: "3",
        Jeudi: "4",
        Vendredi: "5",
        Samedi: "6",
        Dimanche: "7",
      };

      const workingDayIndexes = daysArray
        .map((name) => dayNameToIndex[name])
        .filter(Boolean); // garde seulement les index valides
      setInitialWorkingDays(workingDayIndexes);
    } catch (error: any) {
      // console.log(
      //   "erreur lors de la récupération des jours ouvrables : ",
      //   error
      // );
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };
  // Fonction pour récupérer les séances d'une année
  const fetchEDTSessionsByYear = async () => {
    try {
      if (!selectedYear) return;
      const response = await getSessionsByYear(
        selectedYear?.id,
        buildPermissionContext(Resource.SETTING_EDT, Action.READ),
      );
      // console.log("response data formatted: ",response.data)
      const formattedSessions = response.data.map(
        (session: any, index: number) => ({
          label: session.name,
          start_time: session.start_time?.slice(0, 5), // pour enlever les secondes
          end_time: session.end_time?.slice(0, 5),
          index: index + 1,
          id: session?.id || "",
        }),
      );
      setInitialSeances(formattedSessions);
    } catch (error: any) {
      // console.log("erreur lors de la récupération des sessions : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // Fonction pour récupérer les pauses d'une année
  const fetchEDTBreaksByYear = async () => {
    try {
      if (!selectedYear) return;
      const response = await getBreaksByYear(
        selectedYear?.id,
        buildPermissionContext(Resource.SETTING_EDT, Action.READ),
      );
      // console.log("response data formatted: ",response.data)
      const formattedBreaks = response.data.map(
        (breakItem: any, index: number) => ({
          id: breakItem.id,
          label: breakItem.name,
          start: breakItem.start_time?.slice(0, 5), // pour enlever les secondes
          end: breakItem.end_time?.slice(0, 5),
        }),
      );
      setInitialBreaks(formattedBreaks);
    } catch (error: any) {
      // console.log("erreur lors de la récupération des pauses : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // useEffect pour charger les données quand le modal s'ouvre
  useEffect(() => {
    if (!showSettings) return;
    fetchEDTSettings();
    fetchEDTSessionsByYear();
    fetchEDTBreaksByYear();
  }, [showSettings]);

  useEffect(() => {
    if (!showSettings) return;
    reset({
      workingDays: initialWorkingDays,
      seances: initialSeances,
      breaks: initialBreaks,
    });
  }, [initialBreaks, initialSeances, initialWorkingDays]);
  // Hook pour gérer un tableau dynamique de pauses
  const {
    fields: breakFields,
    append: appendBreak,
    remove: removeBreak,
  } = useFieldArray({
    control,
    name: "breaks",
  });

  // Hook pour gérer un tableau dynamique de séances
  const {
    fields: seanceFields,
    append: appendSeance,
    remove: removeSeance,
  } = useFieldArray({
    control,
    name: "seances",
  });

  const onSubmit = async (data: AcademicSettings) => {
    try {
      if (!selectedYear) return;
      // console.log("data: ", data);

      // Pour comparer deux tableaux sans tenir compte de l'ordre des éléments :
      const haveSameElements = (a: string[], b: string[]) =>
        a.length === b.length && a.every((v) => b.includes(v));

      // sauvegarder les jours ouvrables
      if (!haveSameElements(data.workingDays, initialWorkingDays)) {
        const formatDataForWorksDay = {
          working_days: data.workingDays.map((index) =>
            getDayNameByindex(Number(index)),
          ),
        };
        await updateSetting(formatDataForWorksDay, selectedYear.id);
        setInitialWorkingDays(data.workingDays);
      }

      const haveSameObjects = (a: any[], b: any[]) =>
        a.length === b.length &&
        a.every((v) =>
          b.some(
            (aObj: any) =>
              aObj?.label === v?.label &&
              aObj?.index === v?.index &&
              aObj?.start === v?.start &&
              aObj?.end === v?.end &&
              aObj?.start_time === v?.start_time &&
              aObj?.end_time === v?.end_time &&
              aObj?.id === v?.id,
          ),
        );

      // sauvegarder les sessions
      if (!haveSameObjects(data.seances, initialSeances)) {
        const formattedDataForSessions = data.seances.map((seance: any) => ({
          id: seance?.id || undefined,
          name: seance.label,
          start_time: seance.start_time,
          end_time: seance.end_time,
        }));
        await saveSessionByYear(formattedDataForSessions, selectedYear.id);
        setInitialSeances(data.seances);
      }

      // sauvegarder les pauses
      if (!haveSameObjects(data.breaks || [], initialBreaks)) {
        const formattedDataForBreaks =
          data.breaks?.map((breakItem: any) => ({
            id: breakItem.id,
            start_time: breakItem.start,
            end_time: breakItem.end,
            name: breakItem.label,
          })) || [];
        await saveBreaksByYear(formattedDataForBreaks, selectedYear.id);
        setInitialBreaks(data.breaks || []);
        // console.log("breaks element: ", formattedDataForBreaks);
      }
      toast.success("Paramètre EDT enregistré avec succès");
      onHide();
    } catch (error: any) {
      // console.log(
      //   "erreur lors de l'enregistrement des jours ouvrables : ",
      //   error
      // );
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const academicSettings = watch();

  // useEffect(() => {
  //   console.log(errors);
  // }, [errors]);

  return (
    <Modal show={showSettings} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fas fa-calendar-day me-2"></i>
          Paramètres de l'année universitaire
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleSubmit(onSubmit)}>
          {/* ✅ Jours ouvrables */}
          <Form.Group className="mb-4">
            <Form.Label className="fw-bold text-primary">
              <i className="fa fas fa-calendar-day me-2"></i>
              Jours ouvrables
            </Form.Label>
            <div className="d-flex flex-wrap gap-2 mt-2">
              {[
                "Lundi",
                "Mardi",
                "Mercredi",
                "Jeudi",
                "Vendredi",
                "Samedi",
                "Dimanche",
              ].map((day, index) => {
                const isChecked = academicSettings.workingDays?.includes(
                  String(index + 1),
                );
                return (
                  <React.Fragment key={index}>
                    <input
                      type="checkbox"
                      className="btn-check"
                      id={`day-${index}`}
                      checked={isChecked}
                      onChange={(e) => {
                        const newDays = e.target.checked
                          ? [
                              ...(academicSettings.workingDays || []),
                              String(index + 1),
                            ]
                          : academicSettings.workingDays?.filter(
                              (d) => d !== String(index + 1),
                            );
                        setValue("workingDays", newDays);
                      }}
                    />
                    <label
                      className={`btn ${
                        isChecked ? "btn-primary" : "btn-outline-primary"
                      } px-3 py-2 rounded`}
                      htmlFor={`day-${index}`}
                    >
                      {day}
                    </label>
                  </React.Fragment>
                );
              })}
            </div>
            {errors.workingDays && (
              <Form.Text className="text-danger">
                {errors.workingDays.message}
              </Form.Text>
            )}
          </Form.Group>

          {/* ✅ Séances */}
          <Form.Group className="mb-4">
            <Form.Label className="fw-bold text-success">
              <i className="fa fas fa-clock me-2"></i>
              Séances
            </Form.Label>
            <div style={{ maxHeight: 220, overflowY: "auto" }} className="mt-2">
              {seanceFields.map((seance, index) => (
                <div
                  key={seance.id}
                  className="mb-3 p-3 border rounded shadow-sm bg-light"
                >
                  <div className="d-flex gap-2 mb-2">
                    <Form.Control
                      type="text"
                      placeholder="Libellé"
                      {...register(`seances.${index}.label`)}
                    />
                    <Form.Control
                      type="hidden"
                      {...register(`seances.${index}.index`, {
                        valueAsNumber: true,
                      })}
                      value={index + 1}
                    />
                  </div>
                  <div className="d-flex gap-2 mb-2 align-items-center">
                    <Form.Control
                      type="time"
                      {...register(`seances.${index}.start_time`)}
                    />
                    <Form.Control
                      type="time"
                      {...register(`seances.${index}.end_time`)}
                    />
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeSeance(index)}
                    >
                      <i className="fa fas fa-trash me-1"></i>
                      Supprimer
                    </Button>
                  </div>
                  {errors.seances?.[index] && (
                    <Form.Text className="text-danger">
                      {Object.values(errors.seances[index] || {}).map(
                        (error: any) => (
                          <div key={error.message}>{error.message}</div>
                        ),
                      )}
                    </Form.Text>
                  )}
                </div>
              ))}
            </div>
            <Button
              variant="outline-success"
              size="sm"
              className="w-100"
              onClick={() =>
                appendSeance({
                  // id: String(seanceFields.length + 1),
                  index: seanceFields.length + 1,
                  label: `Séance ${seanceFields.length + 1}`,
                  start_time: "",
                  end_time: "",
                })
              }
            >
              <i className="fa fas fa-plus me-2"></i> Ajouter une séance
            </Button>
            {errors.seances?.root?.message && (
              <Form.Text className="text-danger">
                {errors.seances.root.message}
              </Form.Text>
            )}
          </Form.Group>

          {/* ✅ Pauses dynamiques */}
          <Form.Group className="mb-4">
            <Form.Label className="fw-bold text-warning">
              <i className="fa fas fa-coffee me-2"></i>
              Pauses
            </Form.Label>
            <div style={{ maxHeight: 180, overflowY: "auto" }} className="mt-2">
              {breakFields.map((breakTime, index) => (
                <div
                  key={breakTime.id}
                  className="mb-3 p-3 border rounded shadow-sm bg-light"
                >
                  <div className="d-flex gap-2 mb-2">
                    <Form.Control
                      type="text"
                      placeholder="Libellé"
                      {...register(`breaks.${index}.label`)}
                    />
                  </div>
                  <div className="d-flex gap-2 mb-2 align-items-center">
                    <Form.Control
                      type="time"
                      {...register(`breaks.${index}.start`)}
                    />
                    <Form.Control
                      type="time"
                      {...register(`breaks.${index}.end`)}
                    />
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeBreak(index)}
                    >
                      <i className="fa fas fa-trash me-1"></i> Supprimer
                    </Button>
                  </div>
                  {errors.breaks?.[index] && (
                    <Form.Text className="text-danger">
                      {Object.values(errors.breaks[index] || {}).map(
                        (error: any) => (
                          <div key={error.message}>{error.message}</div>
                        ),
                      )}
                    </Form.Text>
                  )}
                </div>
              ))}
            </div>
            <Button
              variant="outline-warning"
              size="sm"
              className="w-100"
              onClick={() =>
                appendBreak({
                  label: `Pause ${breakFields.length + 1}`,
                  start: "",
                  end: "",
                })
              }
            >
              <i className="fa fas fa-plus me-2"></i>
              Ajouter une pause
            </Button>
            {errors.breaks?.root?.message && (
              <Form.Text className="text-danger">
                {errors.breaks.root.message}
              </Form.Text>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer className="d-flex gap-2">
        <Button
          variant="outline-secondary"
          onClick={() => {
            reset();
            onHide();
          }}
        >
          Annuler
        </Button>
        <Button variant="primary" onClick={handleSubmit(onSubmit)}>
          💾 Enregistrer
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default withPermission(
  EdtParameterModal,
  [
    ...Object.values(Action).map((action) =>
      buildPermission(Resource.SETTING_EDT, action),
    ),
  ],
  "masquer",
);
